import { useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import withAuth from '../../../../components/hoc/withAuth';
import ActionBar from '../../../../components/ui/ActionBar';
import AgSelector from '../../../../components/ui/Selector';
import { AG_STATUS, AGGREGATE_SCORES_LABELS, ALERT_TYPE, REVISED_SCORE_TYPE } from '../../../../constants';
import {
    getAdvisoryGroupData,
    getAdvisoryGroupPotentialMembers,
    getAdvisoryGroupSelectedMembers,
    getRecruitingOutreachByAgId,
    resetPanelSelection,
} from '../../../../ducks/advisoryGroup';
import { AppLoadContext } from '../../../../components/ui/AppLoadContext';
import { AlertContext } from '../../../../context/AlertContext';
import { Button, Modal, Spinner, Tab, Tabs } from 'react-bootstrap';
import styles from './PanelSelectionRecruitment.module.scss';
import Icon from '../../../../components/ui/Icon';
import PanelSelection from './PanelSelection';
import SelectedPanel from './SelectedPanel';
import ProgressBar from '../../../../components/ui/ProgressBar';
import api from "../../../../services/api/advisoryGroupService";
import { dropdownValues } from '../../../../utils/utils';
import messages from '../../../../utils/helper/messages';
import { useLocation } from 'react-router-dom';

const panelAugmentationErrorMessage = (error) => {
    const data = error?.response?.data;
    if (data == null) return error?.message || 'Request failed';
    if (typeof data === 'string') return data;
    return data.message || data.error || data.title || error?.message || 'Request failed';
};

const PanelSelectionRecruitment = () => {
    const dispatch = useDispatch();
    const isAppLoaded = useContext(AppLoadContext);
    const { showAlert } = useContext(AlertContext);
    const location = useLocation();

    const currentUser = useSelector((state) => state.user?.currentUser);
    const advisoryGroups = useSelector((state) => state.advisoryGroup?.masterList);
    const potentialMembers = useSelector((state) => state.advisoryGroup?.panelPotentialMembers);
    const selectedMembers = useSelector((state) => state.advisoryGroup?.panelSelectedMembers);
    const panelProximityVenues = useSelector((state) => state.advisoryGroup?.panelProximityVenues);
    const panelProximityDefaultLocationId = useSelector(
        (state) => state.advisoryGroup?.panelProximityDefaultLocationId,
    );

    const [activeKey, setActiveKey] = useState('panelSelection');
    const [showInstructions, setShowInstructions] = useState(false);
    const [selectedAgId, setSelectedAgId] = useState(0);
    const [currentRepScore, setCurrentRepScore] = useState(null);
    const [callDispositionData, setCallDispositionData] = useState(null);
    const [agList, setAgList] = useState([]);
    const [augmentLoading, setAugmentLoading] = useState(false);
    const [showAugmentConfirm, setShowAugmentConfirm] = useState(false);
    const [proximityVenueSelection, setProximityVenueSelection] = useState('');

    useLayoutEffect(() => {
        setProximityVenueSelection('');
        // Clear proximity fields before paint so child queries do not reuse another AG’s default locationId.
        dispatch(resetPanelSelection());
    }, [selectedAgId, dispatch]);

    useEffect(() => {
        if (location?.state?.agId) {
            setSelectedAgId(location.state.agId);
        }
        dispatch(resetPanelSelection());
    }, [location.pathname]);

    useEffect(() => {
        if (isAppLoaded) {
            if (!currentUser) {
                return;
            }
            dispatch(getAdvisoryGroupData(currentUser?.companyId));
            api.GetMemberCallDispositionData().then((response) => {
                const { data } = response;
                setCallDispositionData(data?.length > 0 ? dropdownValues(data, 'callDisposition', undefined, true) : []);
            });
        }
    }, [isAppLoaded, dispatch, currentUser]);

    useEffect(() => {
        if (isAppLoaded && advisoryGroups?.length > 0) {
            const activeAgs = advisoryGroups.filter(
                (ag) => ag.status === AG_STATUS.Active || ag.status === AG_STATUS.New
            );
            setAgList(activeAgs);
        }
    }, [advisoryGroups]);

    const handleSelectChange = (e) => {
        const selectedAg = Number(e.target.value);
        setSelectedAgId(selectedAg);
        fetchRepScore(selectedAg);
    };

    const fetchRepScore = (agId) => {
        api.GetCurrentRepScore(agId, REVISED_SCORE_TYPE.PanelScore).then((res) => {
            setCurrentRepScore(res.data[0]?.aggregateScoreNumeric || 0);
        });
    };

    const firstVenueLocationId =
        panelProximityVenues?.[0]?.locationId != null && String(panelProximityVenues[0].locationId).trim() !== ''
            ? String(panelProximityVenues[0].locationId).trim()
            : '';

    const effectivePanelProximityLocationId = useMemo(() => {
        const sel = proximityVenueSelection && String(proximityVenueSelection).trim();
        if (sel) return sel;
        if (panelProximityDefaultLocationId != null && String(panelProximityDefaultLocationId).trim() !== '') {
            return String(panelProximityDefaultLocationId).trim();
        }
        return firstVenueLocationId;
    }, [proximityVenueSelection, panelProximityDefaultLocationId, firstVenueLocationId]);

    const proximityMembersQuery = () =>
        effectivePanelProximityLocationId ? { locationId: effectivePanelProximityLocationId } : {};

    const handleEdit = () => {
        dispatch(getAdvisoryGroupPotentialMembers(selectedAgId, proximityMembersQuery()));
        dispatch(getAdvisoryGroupSelectedMembers(selectedAgId));
        fetchRepScore(selectedAgId);
    };

    const refreshPanelLists = () => {
        dispatch(getAdvisoryGroupPotentialMembers(selectedAgId, proximityMembersQuery()));
        dispatch(getAdvisoryGroupSelectedMembers(selectedAgId));
        dispatch(getRecruitingOutreachByAgId(selectedAgId));
        fetchRepScore(selectedAgId);
    };

    const runPanelAugmentation = () => {
        if (!selectedAgId || augmentLoading) return;
        setShowAugmentConfirm(false);
        setAugmentLoading(true);
        const body =
            currentUser?.id != null && !Number.isNaN(Number(currentUser.id))
                ? { userId: Number(currentUser.id) }
                : {};
        api.PanelAugmentation(selectedAgId, body)
            .then((res) => {
                const payload = res?.data;
                if (payload?.success !== true) {
                    showAlert(
                        payload?.message || 'Panel augmentation did not complete successfully.',
                        ALERT_TYPE.ERROR
                    );
                    return;
                }
                if (payload.skipped === true) {
                    showAlert(
                        payload.message || 'Panel augmentation was skipped.',
                        ALERT_TYPE.SUCCESS
                    );
                } else {
                    let msg = payload.message || 'Panel augmented successfully.';
                    if (payload.inserted != null && payload.inserted !== '') {
                        msg += ` (${payload.inserted} placeholder(s) requested.)`;
                    }
                    showAlert(msg, ALERT_TYPE.SUCCESS);
                }
                refreshPanelLists();
            })
            .catch((error) => {
                const status = error?.response?.status;
                const msg = panelAugmentationErrorMessage(error);
                showAlert(
                    `${messages.error.prefix} ${msg}${status ? ` (${status})` : ''}.`,
                    ALERT_TYPE.ERROR
                );
            })
            .finally(() => {
                setAugmentLoading(false);
            });
    };

    const tabsContent = [
        {
            eventKey: 'panelSelection',
            title: 'Panel Selection',
        },
        {
            eventKey: 'selectedPanel',
            title: 'Selected Panel',
        },
    ];

    const percentage = Math.max(0, Math.min(currentRepScore, 4)) * 25;

    return (
        <>
            <div className='bg-white'>
                <div className="position-relative mb-3">
                    <div
                        className={`position-relative bg-primary text-white d-flex rounded-top border-0 ${styles.tableAlertHeadertxt}`}>
                        <div className="position-absolute start-50 translate-middle-x fw-bold pt-2">
                            Panel Selection
                        </div>
                        <div className="position-absolute end-0 top-50 translate-middle-y me-3 d-flex align-items-center gap-2">
                            <Button
                                type="button"
                                variant="light"
                                size="sm"
                                className="text-primary fw-semibold py-0"
                                disabled={selectedAgId === 0 || augmentLoading}
                                onClick={() => setShowAugmentConfirm(true)}
                            >
                                {augmentLoading ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-1" />
                                        Loading…
                                    </>
                                ) : (
                                    'Load More Candidates'
                                )}
                            </Button>
                            <Icon
                                icon="info-solid"
                                size={18}
                                onMouseEnter={() => setShowInstructions(true)}
                                onMouseLeave={() => setShowInstructions(false)}
                                className="cursor-pointer text-white"
                            />
                        </div>
                    </div>
                    {showInstructions && (
                        <div className={`position-absolute start-0 end-0 z-1 bg-white p-4 ${styles.instructionsBox}`}>
                            <div className="d-flex justify-content-between">
                                <div className="d-flex small">
                                    Upon selecting an advisory group, a list of candidates will display in recommended order based on how each candidate contributes to building a representative panel. Use the Panel fit column (score and info icon) for what the number means and how it relates to the colored bar above. This display is automatically updated as candidates are added or removed from the panel. Once a member accepts and enrolls, the member will be moved to the Selected Panel tab.
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="d-flex flex-column flex-lg-row align-items-start mb-3 px-3">
                    <div className={`${styles.selectorWrap} flex-shrink-0`}>
                        <ActionBar back={true}>
                            <AgSelector
                                items={agList}
                                param="shortName"
                                value={selectedAgId}
                                placeholder="Select Advisory Group"
                                noRecordMessage="No advisory groups"
                                onSelectChange={handleSelectChange}
                            />
                        </ActionBar>
                    </div>
                    <div className="flex-grow-1 mt-2">
                        <Tabs
                            defaultActiveKey="panelSelection"
                            activeKey={activeKey}
                            onSelect={(k) => setActiveKey(k)}
                            variant="underline"
                            className="tabs">
                            {tabsContent.map((content) => (
                                <Tab
                                    key={content.eventKey}
                                    eventKey={content.eventKey}
                                    title={content.title}
                                />
                            ))}
                        </Tabs>
                    </div>
                </div>
                <div>
                    {(potentialMembers?.length > 0 || selectedMembers?.length > 0) &&
                        <ProgressBar labels={AGGREGATE_SCORES_LABELS} percentage={percentage} />}
                </div>
                {activeKey === 'panelSelection' && (
                    <PanelSelection
                        selectedAgId={selectedAgId}
                        cohortId={agList.find((a) => a.id === selectedAgId)?.cohortId ?? null}
                        callDispositionData={callDispositionData}
                        onEdit={handleEdit}
                        proximityVenueSelection={proximityVenueSelection}
                        onProximityVenueSelectionChange={setProximityVenueSelection}
                    />
                )}
                {activeKey === 'selectedPanel' && (
                    <SelectedPanel selectedAgId={selectedAgId} />
                )}
            </div>

            <Modal show={showAugmentConfirm} onHide={() => setShowAugmentConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Load more candidates</Modal.Title>
                </Modal.Header>
                <Modal.Body>{messages.confirm.panelAugmentationConfirm}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAugmentConfirm(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={runPanelAugmentation} disabled={augmentLoading}>
                        Continue
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default withAuth(PanelSelectionRecruitment);
